import Phaser from 'phaser';

/**
 * A DOM text box laid over the FIT-scaled canvas (the codes panel's recipe):
 * placed in game coordinates, re-placed on resize, swallowing every pointer
 * and key event so Phaser underneath never sees them, and removed when the
 * scene shuts down.
 */
export function canvasTextInput(
  scene: Phaser.Scene,
  box: { x: number; y: number; w: number; h: number },
  opts: { maxLength: number; placeholder: string; testId: string; value?: string },
  onChange: (value: string) => void,
): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'text';
  return mountCanvasField(scene, input, box, opts, onChange, 22);
}

/** The same, several lines tall (a level's description). */
export function canvasTextArea(
  scene: Phaser.Scene,
  box: { x: number; y: number; w: number; h: number },
  opts: { maxLength: number; placeholder: string; testId: string; value?: string },
  onChange: (value: string) => void,
): HTMLTextAreaElement {
  const area = document.createElement('textarea');
  area.rows = 3;
  Object.assign(area.style, { resize: 'none', lineHeight: '1.25' });
  return mountCanvasField(scene, area, box, opts, onChange, 17);
}

function mountCanvasField<T extends HTMLInputElement | HTMLTextAreaElement>(
  scene: Phaser.Scene,
  input: T,
  box: { x: number; y: number; w: number; h: number },
  opts: { maxLength: number; placeholder: string; testId: string; value?: string },
  onChange: (value: string) => void,
  fontPx: number,
): T {
  input.maxLength = opts.maxLength;
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.placeholder = opts.placeholder;
  input.value = opts.value ?? '';
  input.dataset.testid = opts.testId;
  input.setAttribute('aria-label', opts.placeholder);
  Object.assign(input.style, {
    position: 'fixed',
    zIndex: '20',
    background: '#0b0a12',
    border: '2px solid #2fb8a6',
    borderRadius: '6px',
    outline: 'none',
    color: '#ffffff',
    fontFamily: 'system-ui, sans-serif',
    fontWeight: '600',
    padding: input instanceof HTMLTextAreaElement ? '8px 12px' : '0 14px',
    boxSizing: 'border-box',
  } satisfies Partial<CSSStyleDeclaration>);
  for (const ev of [
    'pointerdown',
    'pointerup',
    'mousedown',
    'mouseup',
    'touchstart',
    'touchend',
    'wheel',
  ]) {
    input.addEventListener(ev, (e) => e.stopPropagation());
  }
  input.addEventListener('keydown', (e) => e.stopPropagation());
  input.addEventListener('keyup', (e) => e.stopPropagation());
  input.addEventListener('input', () => onChange(input.value));
  (scene.game.canvas.parentElement ?? document.body).append(input);

  const place = () => {
    const rect = scene.game.canvas.getBoundingClientRect();
    const s = rect.width / 1280;
    input.style.left = `${rect.left + box.x * s}px`;
    input.style.top = `${rect.top + box.y * s}px`;
    input.style.width = `${box.w * s}px`;
    input.style.height = `${box.h * s}px`;
    input.style.fontSize = `${Math.max(11, fontPx * s)}px`;
  };
  place();
  window.addEventListener('resize', place);
  scene.scale.on(Phaser.Scale.Events.RESIZE, place);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    window.removeEventListener('resize', place);
    scene.scale.off(Phaser.Scale.Events.RESIZE, place);
    input.remove();
  });
  return input;
}
